import FetchSubjects from '@Application/Subjects/Queries/FetchSubjects';
import fetchSubjects from '@Application/Subjects/fetchSubjects';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { dataViewGridPt } from '@Guidelines/Theme/commonPassthrough';
import SubjectCard from '@UI/Pages/Subjects/SubjectList/SubjectCard';
import SubjectFilterView from '@UI/Pages/Subjects/SubjectList/Filters/SubjectFilterView';
import subjectFiltersSignal, {
	setSubjectFilters,
} from '@UI/Pages/Subjects/SubjectList/Filters/Signals/subjectFiltersSignal';
import { openCreateSubjectSidebar } from '@UI/Pages/Subjects/Signals/createSubjectSidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import type SubjectView from '@UI/Store/Subjects/Views/SubjectView';
import SubjectListContainer from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectListContainer';
import { FolderOpen, Plus, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { CACHE_DISABLED, useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, DataView as SubjectsDataView, type DataViewPageEvent, Skeleton } from '@verclam/ui-kit';

const SKELETON_CARD_COUNT = 3;
const skeletonCardIndexes = [...Array(SKELETON_CARD_COUNT).keys()];

type Props = {
	readonly type: SubjectTypeView;
};

/**
 * Owns the fetch and every loading/error/empty/nominal branch of the dossier list of `type`. The
 * skeletons and the error come before `DataView`, which would otherwise draw its empty message while
 * the answer is still pending; `error !== null` is checked before the data-absent branch, never the
 * reverse (`guides/state-stores.md`).
 */
const SubjectsListContent: React.FC<Props> = ({ type }) => {
	const dispatch = useDispatch();
	const filters = useSignalValue(subjectFiltersSignal);
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const { search, page, rows, fieldFilters, fieldSort, includeArchived } = filters;

	const {
		data: loadedSubjects,
		isLoading,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.subjectsStore.subjects,
		stopOn: (value) =>
			value?.matches(type.id, search, page, rows, fieldFilters, fieldSort, includeArchived) === true,
		query: () => {
			if (accountId === null) {
				return;
			}

			return dispatch(
				fetchSubjects(
					new FetchSubjects(
						accountId,
						type.id,
						search,
						page,
						rows,
						fieldFilters,
						fieldSort,
						includeArchived,
					),
				),
			);
		},
		cache: CACHE_DISABLED,
		deps: [accountId, type.id, search, page, rows, fieldFilters, fieldSort, includeArchived],
	});

	// The store keeps the last list read: it may be another type's while this one loads.
	const subjects = loadedSubjects !== null && loadedSubjects.typeId === type.id ? loadedSubjects : null;

	const handleResetFiltersClick = React.useCallback(() => {
		setSubjectFilters(SubjectFilterView.createDefault(type.id));
	}, [type.id]);

	const handlePageChange = React.useCallback((event: DataViewPageEvent) => {
		setSubjectFilters(subjectFiltersSignal.peek().setPageAndRows(event.page, event.rows));
	}, []);

	const renderSubjectCard = React.useCallback(
		(subject: SubjectView) => <SubjectCard subject={subject} typeKey={type.key} fields={type.fields} />,
		[type.key, type.fields],
	);

	const emptyMessage = React.useMemo(() => {
		const style = {
			height: '100%',
		};

		if (filters.hasActiveFilters()) {
			return (
				<EmptyState
					title="Aucun résultat"
					icon={<TriangleAlert size={28} />}
					action={
						<Button
							type="button"
							text
							label="Réinitialiser les filtres"
							onClick={handleResetFiltersClick}
						/>
					}
					style={style}
				/>
			);
		}

		return (
			<EmptyState
				title={type.wording.emptyList}
				icon={<FolderOpen size={28} />}
				action={
					<Button
						type="button"
						label={type.wording.createAction}
						icon={<Plus size={16} />}
						onClick={openCreateSubjectSidebar}
					/>
				}
				style={style}
			/>
		);
	}, [filters, type.wording.emptyList, type.wording.createAction, handleResetFiltersClick]);

	if (accountId === null || error !== null) {
		return (
			<ErrorState
				title="Impossible de charger les dossiers"
				onRetry={accountId === null ? undefined : refetch}
			/>
		);
	}

	if (subjects === null) {
		return (
			<SubjectListContainer>
				{skeletonCardIndexes.map((index) => (
					<Skeleton key={index} height="6rem" animation="wave" shape="rectangle" />
				))}
			</SubjectListContainer>
		);
	}

	return (
		<SubjectListContainer>
			<SubjectsDataView
				emptyMessage={emptyMessage}
				loading={isLoading}
				value={subjects.subjects}
				layout="grid"
				dataKey="id"
				itemTemplate={renderSubjectCard}
				lazy
				paginator
				pt={dataViewGridPt}
				first={page * rows}
				rows={rows}
				rowsPerPageOptions={[10, 25, 50, 100]}
				totalRecords={subjects.totalRecords}
				onPage={handlePageChange}
			/>
		</SubjectListContainer>
	);
};

export default SubjectsListContent;
